import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { useBranch } from '../context/BranchContext';

const COLORS = ['#1A3A5C', '#2CB8A0', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#6366F1'];

const KpiCard = ({ title, value, subtitle, color = 'primary.main', footer }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="h4" fontWeight={800} color={color} sx={{ my: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block">
          {subtitle}
        </Typography>
      )}
      {footer && (
        <>
          <Divider sx={{ my: 1 }} />
          {footer}
        </>
      )}
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children, action }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {action}
      </Box>
      <Box sx={{ flex: 1, minHeight: 260 }}>
        {children}
      </Box>
    </CardContent>
  </Card>
);

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const fmtMonthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1).toLocaleDateString('es-CL', { month: 'short', year: 'numeric' });
};

const MONTHS = [
  { value: 'todos', label: 'Todos' },
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

export default function HospitalEgresosStatsTab({ egresos, patients, hospitalizations }) {
  const { filterByBranch } = useBranch();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('todos');
  const [selectedMonth, setSelectedMonth] = useState('todos');

  useEffect(() => {
    setLoading(!Array.isArray(egresos) || !Array.isArray(patients));
  }, [egresos, patients]);

  const patientsMap = useMemo(
    () => Object.fromEntries((patients || []).map((p) => [p.id, p])),
    [patients]
  );
  const hospitalizationsMap = useMemo(
    () => Object.fromEntries((hospitalizations || []).map((h) => [h.id, h])),
    [hospitalizations]
  );

  const filteredEgresos = useMemo(() => {
    const withBranch = (egresos || []).map((eg) => {
      const h = hospitalizationsMap[eg.hospitalizationId];
      const p = patientsMap[eg.patientId];
      return {
        ...eg,
        branchId: h?.branchId || p?.branchId || '',
        species: p?.species || 'Sin especie',
      };
    });
    return filterByBranch(withBranch, { includeUnassigned: true });
  }, [egresos, patientsMap, hospitalizationsMap, filterByBranch]);

  const yearOptions = useMemo(() => {
    const years = new Set();
    filteredEgresos.forEach((eg) => {
      if (eg.fechaAlta) years.add(eg.fechaAlta.slice(0, 4));
    });
    return ['todos', ...Array.from(years).sort().reverse()];
  }, [filteredEgresos]);

  const periodEgresos = useMemo(() => {
    if (selectedYear === 'todos' && selectedMonth === 'todos') return filteredEgresos;
    return filteredEgresos.filter((eg) => {
      if (!eg.fechaAlta) return false;
      const y = eg.fechaAlta.slice(0, 4);
      const m = eg.fechaAlta.slice(5, 7);
      if (selectedYear !== 'todos' && y !== selectedYear) return false;
      if (selectedMonth !== 'todos' && m !== selectedMonth) return false;
      return true;
    });
  }, [filteredEgresos, selectedYear, selectedMonth]);

  const stats = useMemo(() => {
    const total = periodEgresos.length;
    if (total === 0) {
      return {
        total: 0,
        avgLos: 0,
        medianLos: 0,
        mortalityRate: 0,
        euthanasiaRate: 0,
        deathEuthRate: 0,
        readmissionRate: 0,
        complicationRate: 0,
        postQxRate: 0,
        surgicalRate: 0,
        homeDischargeRate: 0,
        conditionsChart: [],
        losChart: [],
        monthlyChart: [],
        topDiagnoses: [],
        topProcedures: [],
        complicationTypes: [],
        deathCauses: [],
        speciesChart: [],
      };
    }

    const daysList = periodEgresos
      .map((eg) => Number(eg.diasHospitalizacion))
      .filter((d) => Number.isFinite(d) && d >= 0)
      .sort((a, b) => a - b);
    const totalDays = daysList.reduce((s, d) => s + d, 0);
    const avgLos = totalDays / (daysList.length || 1);
    const medianLos = daysList.length ? daysList[Math.floor(daysList.length / 2)] : 0;

    const fallecidos = periodEgresos.filter((eg) => eg.fallecido).length;
    const eutanasias = periodEgresos.filter((eg) => eg.condicionAlta === 'Eutanasia').length;
    const muerteEutanasia = periodEgresos.filter(
      (eg) => eg.condicionAlta === 'Fallecimiento' || eg.condicionAlta === 'Eutanasia'
    ).length;
    const reingresos = periodEgresos.filter((eg) => eg.reingreso).length;
    const complicados = periodEgresos.filter((eg) => eg.complicaciones).length;
    const postQx = periodEgresos.filter((eg) => eg.complicacionPostQx).length;
    const quirurgicos = periodEgresos.filter((eg) => eg.casoQuirurgico).length;
    const altasDomicilio = periodEgresos.filter((eg) =>
      normalize(eg.condicionAlta).includes('domicilio')
    ).length;

    const countBy = (keyFn) => {
      const counts = {};
      periodEgresos.forEach((eg) => {
        const key = keyFn(eg) || 'Sin registro';
        counts[key] = (counts[key] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
    };

    const conditionsChart = countBy((eg) => eg.condicionAlta || 'No registrada');
    const losChart = [
      { name: '0-1 día', value: daysList.filter((d) => d <= 1).length },
      { name: '2-3 días', value: daysList.filter((d) => d >= 2 && d <= 3).length },
      { name: '4-7 días', value: daysList.filter((d) => d >= 4 && d <= 7).length },
      { name: '8-14 días', value: daysList.filter((d) => d >= 8 && d <= 14).length },
      { name: '>14 días', value: daysList.filter((d) => d > 14).length },
    ];

    const monthly = {};
    const allMonths = new Set();
    periodEgresos.forEach((eg) => {
      if (!eg.fechaAlta) return;
      const ym = eg.fechaAlta.slice(0, 7);
      monthly[ym] = (monthly[ym] || 0) + 1;
      allMonths.add(ym);
    });

    if (selectedYear !== 'todos') {
      for (let m = 1; m <= 12; m++) {
        const key = `${selectedYear}-${String(m).padStart(2, '0')}`;
        allMonths.add(key);
      }
    }

    const monthlyChart = Array.from(allMonths)
      .sort((a, b) => a.localeCompare(b))
      .map((ym) => ({ name: fmtMonthLabel(ym), value: monthly[ym] || 0 }));

    const getDiagnosisName = (eg) => {
      const d = eg.diagnosticoEgreso || eg.diagnosticoPrincipal || {};
      return d.name || d.diagnosis || eg.altaDiagnosticoEgreso || 'Sin diagnóstico';
    };
    const topDiagnoses = countBy(getDiagnosisName).slice(0, 8);

    const topProcedures = countBy((eg) => eg.procedimientoPrincipal?.name || 'Sin procedimiento')
      .filter((p) => p.name !== 'Sin procedimiento')
      .slice(0, 8);

    const complicationTypes = {};
    periodEgresos.forEach((eg) => {
      (eg.tiposComplicaciones || []).forEach((t) => {
        complicationTypes[t] = (complicationTypes[t] || 0) + 1;
      });
    });
    const complicationTypesChart = Object.entries(complicationTypes)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const deathCauses = countBy((eg) =>
      eg.condicionAlta === 'Fallecimiento' || eg.condicionAlta === 'Eutanasia'
        ? eg.causaMuerte || 'No registrada'
        : null
    ).filter((c) => c.name !== 'Sin registro' && c.name !== 'No registrada');

    const speciesChart = countBy((eg) => (eg.species || 'Sin especie'));

    return {
      total,
      avgLos: avgLos.toFixed(1),
      medianLos,
      mortalityRate: ((fallecidos / total) * 100).toFixed(1),
      euthanasiaRate: ((eutanasias / total) * 100).toFixed(1),
      deathEuthRate: ((muerteEutanasia / total) * 100).toFixed(1),
      readmissionRate: ((reingresos / total) * 100).toFixed(1),
      complicationRate: ((complicados / total) * 100).toFixed(1),
      postQxRate: ((postQx / total) * 100).toFixed(1),
      surgicalRate: ((quirurgicos / total) * 100).toFixed(1),
      homeDischargeRate: ((altasDomicilio / total) * 100).toFixed(1),
      conditionsChart,
      losChart,
      monthlyChart,
      topDiagnoses,
      topProcedures,
      complicationTypes: complicationTypesChart,
      deathCauses,
      speciesChart,
    };
  }, [periodEgresos, selectedYear]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (stats.total === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No hay egresos hospitalarios para el período seleccionado.
      </Alert>
    );
  }

  const periodLabel =
    selectedYear === 'todos' && selectedMonth === 'todos'
      ? 'Todo el período'
      : selectedYear === 'todos'
      ? `Mes: ${MONTHS.find((m) => m.value === selectedMonth)?.label || selectedMonth}`
      : selectedMonth === 'todos'
      ? `Año ${selectedYear}`
      : `${MONTHS.find((m) => m.value === selectedMonth)?.label || selectedMonth} ${selectedYear}`;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={800}>
          Estadísticas de Egresos Hospitalarios
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Indicadores de calidad clínica basados en la casuística de alta
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="egreso-year-label">Año</InputLabel>
              <Select
                labelId="egreso-year-label"
                value={selectedYear}
                label="Año"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                {yearOptions
                  .filter((y) => y !== 'todos')
                  .map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="egreso-month-label">Mes</InputLabel>
              <Select
                labelId="egreso-month-label"
                value={selectedMonth}
                label="Mes"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {MONTHS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 6 }}>
            <Chip label={periodLabel} color="primary" size="small" />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {stats.total} egresos en el período
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Total egresos"
            value={stats.total}
            subtitle="Altas registradas"
            color="primary.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Estancia promedio"
            value={`${stats.avgLos} días`}
            subtitle={`Mediana: ${stats.medianLos} días`}
            color="info.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Mortalidad + Eutanasia"
            value={`${stats.deathEuthRate}%`}
            subtitle={`Mortalidad: ${stats.mortalityRate}% · Eutanasia: ${stats.euthanasiaRate}%`}
            color={Number(stats.deathEuthRate) > 5 ? 'error.main' : 'text.primary'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Reingresos"
            value={`${stats.readmissionRate}%`}
            subtitle="Pacientes reinternados"
            color="warning.main"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Complicaciones"
            value={`${stats.complicationRate}%`}
            subtitle={`Post-Qx: ${stats.postQxRate}%`}
            color={Number(stats.complicationRate) > 5 ? 'error.main' : 'success.main'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Casos quirúrgicos"
            value={`${stats.surgicalRate}%`}
            subtitle="Proporción de egresos con procedimiento"
            color="secondary.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Alta a domicilio"
            value={`${stats.homeDischargeRate}%`}
            subtitle="Egresos ambulatorios exitosos"
            color="success.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Promedio de días"
            value={stats.avgLos}
            subtitle="ALOS (Average Length of Stay)"
            color="text.primary"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title="Tendencia mensual de egresos">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.monthlyChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="value" name="Egresos" stroke={COLORS[0]} strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Condición de alta"
            action={<Chip size="small" label="Distribución" color="primary" />}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.conditionsChart}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={3}
                  label
                >
                  {stats.conditionsChart.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title="Distribución de estancia (ALOS)">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.losChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" name="Pacientes" radius={[4, 4, 0, 0]}>
                  {stats.losChart.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Principales diagnósticos de egreso"
            action={<Chip size="small" label="Top 8" color="secondary" />}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={stats.topDiagnoses}
                layout="vertical"
                margin={{ top: 8, right: 16, left: 40, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" name="Egresos" radius={[0, 4, 4, 0]}>
                  {stats.topDiagnoses.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Principales procedimientos"
            action={<Chip size="small" label="Top 8" color="secondary" />}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={stats.topProcedures}
                layout="vertical"
                margin={{ top: 8, right: 16, left: 40, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" name="Egresos" radius={[0, 4, 4, 0]}>
                  {stats.topProcedures.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title="Tipos de complicaciones">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.complicationTypes} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" name="Casos" radius={[4, 4, 0, 0]}>
                  {stats.complicationTypes.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {stats.deathCauses.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <ChartCard title="Causas de muerte / eutanasia">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.deathCauses}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    innerRadius={50}
                    paddingAngle={3}
                    label
                  >
                    {stats.deathCauses.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Egresos por especie"
            action={<Chip size="small" label="Casuística" color="primary" />}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.speciesChart}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={3}
                  label
                >
                  {stats.speciesChart.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
